/**
 * Protected Route Component
 * 
 * Wrapper component that requires authentication to access certain routes
 */

import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { LoginForm } from './LoginForm';
import { Box, CircularProgress, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  requireAdminPanel?: boolean; // New prop for admin panel access
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  adminOnly = false,
  requireAdminPanel = false
}) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        height="100vh"
        gap={2}
      >
        <CircularProgress size={50} />
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  // Check for legacy admin access
  if (adminOnly && !isAdmin) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        height="100vh"
        gap={2}
        sx={{ p: 4 }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          This page requires administrator access. 
          Please contact support if you believe this is an error.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Current user: {user.email} (Role: {user.role || 'customer'})
        </Typography>
      </Box>
    );
  }

  // Check for admin panel access (API customer or above)
  if (requireAdminPanel && !authService.canAccessAdminPanel()) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        height="100vh"
        gap={2}
        sx={{ p: 4 }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          Admin Panel Access Required
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          This page requires admin panel access (API Customer role or higher). 
          Please contact support if you believe this is an error.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Current user: {user.email} (Role: {user.role || 'customer'})
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};